// frontend/src/pages/NewUIGames.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Trophy, AlertCircle } from 'lucide-react';
import axios from '../config/axios';
import GoogleAdBanner from '../components/newui/GoogleAdBanner';

const GameStatBadge = ({ icon: Icon, value, label, className = "" }) => (
    <div className={`flex items-center ${className}`}>
        <Icon className="w-4 h-4 mr-1" />
        <span className="text-sm font-semibold">{value} {label}</span>
    </div>
);

const FeaturedGameCard = ({ game, navigate }) => (
    <div
        onClick={() => navigate(`/games/${game.slug}`)}
        className="relative bg-[#2284DF] rounded-2xl shadow-lg p-8 flex flex-col lg:flex-row justify-between items-center cursor-pointer transform transition-all hover:scale-[1.02] hover:shadow-xl overflow-hidden"
    >
        {/* Textura de fondo */}
        <div
            className="absolute inset-0 pointer-events-none z-0"
            style={{
                backgroundColor: "#1F79CF",
                WebkitMaskImage: "url(/images/backgrounds/textura-azul.png)",
                maskImage: "url(/images/backgrounds/textura-azul.png)",
                WebkitMaskRepeat: "repeat",
                maskRepeat: "repeat",
                WebkitMaskSize: "1769px 1183px",
                maskSize: "1769px 1183px",
                WebkitMaskPosition: "center",
                maskPosition: "center",
            }}
            aria-hidden="true"
        />

        {/* Contenido */}
        <div className="relative z-10 w-full lg:w-1/2 space-y-6 mb-8 lg:mb-0">
            <img src={game.logo} alt={game.name} className="h-16 object-contain" />

            <div className="space-y-4">
                <p className="text-white/90 text-lg leading-relaxed">{game.description}</p>

                <div className="flex flex-wrap items-center gap-6">
                    <GameStatBadge
                        icon={Trophy}
                        value={game.stats.openTournaments}
                        label="torneos abiertos"
                        className="text-[#fce503]"
                    />
                    <GameStatBadge
                        icon={Users}
                        value={game.stats.ongoingTournaments}
                        label="en curso"
                        className="text-[#fce503]"
                    />
                </div>
            </div>

            <button className="px-8 py-3 bg-[#fce503] text-[#122436] rounded-xl font-extrabold uppercase text-sm tracking-wide hover:bg-[#fce503]/90 transition-colors">
                Jugar ahora
            </button>
        </div>

        <div className="relative z-10 w-full lg:w-1/2">
            <img
                src={game.featuredImg}
                alt={`${game.name} featured`}
                className="w-full h-auto object-contain"
            />
        </div>
    </div>
);

const GameCard = ({ game, navigate }) => (
    <div
        onClick={() => navigate(`/games/${game.slug}`)}
        className="relative bg-[#2284DF] rounded-2xl shadow-lg p-6 flex flex-col space-y-4 cursor-pointer transform transition-all hover:scale-[1.02] hover:shadow-xl min-h-[240px] overflow-hidden"
    >
        {/* Textura de fondo */}
        <div
            className="absolute inset-0 pointer-events-none z-0"
            style={{
                backgroundColor: "#1F79CF",
                WebkitMaskImage: "url(/images/backgrounds/textura-azul.png)",
                maskImage: "url(/images/backgrounds/textura-azul.png)",
                WebkitMaskRepeat: "repeat",
                maskRepeat: "repeat",
                WebkitMaskSize: "1769px 1183px",
                maskSize: "1769px 1183px",
                WebkitMaskPosition: "center",
                maskPosition: "center",
            }}
            aria-hidden="true"
        />

        {/* Contenido */}
        <img src={game.logo} alt={game.name} className="relative z-10 h-12 object-contain" />
        <p className="relative z-10 text-white/90 text-sm flex-grow">{game.shortDesc}</p>

        <div className="relative z-10 flex items-center gap-6 mt-auto pt-4 border-t border-white/20">
            <GameStatBadge
                icon={Trophy}
                value={game.stats.openTournaments}
                label="torneos"
                className="text-white/90"
            />
            <GameStatBadge
                icon={Users}
                value={game.stats.ongoingTournaments}
                label="en curso"
                className="text-white/90"
            />
        </div>
    </div>
);

const NewUIGames = () => {
    const navigate = useNavigate();
    const [games, setGames] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Aplicar clase de fuente y overflow
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        document.body.style.overflowX = 'hidden';
        return () => {
            document.body.classList.remove('acumin-scope');
            document.body.style.overflowX = '';
        };
    }, []);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const response = await axios.get('/games');
                setGames(response.data.data);
            } catch (error) {
                setError(error.response?.data?.message || 'Error al cargar los juegos');
            } finally {
                setIsLoading(false);
            }
        };

        fetchGames();
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#122436] flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#122436] flex items-center justify-center p-4">
                <div className="bg-red-50 text-red-600 p-6 rounded-2xl flex items-center max-w-md shadow-lg">
                    <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                    <p className="font-semibold">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#122436] overflow-x-hidden">
            <div className="max-w-[calc(100%-100px)] mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
                <h1 className="toon text-4xl md:text-5xl text-white uppercase tracking-wider mb-8">
                    Juegos
                </h1>

                <div className="space-y-12">
                    {/* Featured Game */}
                    {games.featured.length > 0 && (
                        <section>
                            <h2 className="toon text-3xl md:text-4xl text-white uppercase mb-6">
                                Destacado
                            </h2>
                            <FeaturedGameCard game={games.featured[0]} navigate={navigate} />
                        </section>
                    )}

                    {/* Active Games */}
                    {games.active.length > 0 && (
                        <section>
                            <h2 className="toon text-3xl md:text-4xl text-white uppercase mb-6">
                                Juegos Disponibles
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {games.active.map(game => (
                                    <GameCard key={game.id} game={game} navigate={navigate} />
                                ))}
                            </div>
                        </section>
                    )}

                    <div className="pt-2">
                        <GoogleAdBanner
                            slot={process.env.REACT_APP_ADSENSE_GAMES_MID_SLOT || process.env.REACT_APP_ADSENSE_GAMES_TOP_SLOT}
                            className="max-w-5xl mx-auto"
                            minHeight={150}
                            compact
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewUIGames;